import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, MapPin } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/data/content";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", message: "" };

const fieldClass =
  "w-full border-b border-border bg-transparent py-4 text-sm text-offwhite placeholder:text-mist/50 transition-colors duration-300 focus:border-aqua-bright focus:outline-none";

export const ContactSection = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (event) => {
    setValues((prev) => ({ ...prev, [key]: event.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (values.name.trim().length < 2) next.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim()))
      next.email = "Please enter a valid email address";
    if (values.message.trim().length < 10)
      next.message = "Please tell us a little more (10 characters minimum)";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: values.name.trim(),
        email: values.email.trim(),
        company: values.company.trim() || null,
        message: values.message.trim(),
      });
      setValues(EMPTY);
      setSent(true);
    } catch (error) {
      toast.error("Your message could not be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-border/70 bg-navy-900/40 py-24 lg:py-36"
    >
      <div className="shell grid gap-16 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow={CONTACT.eyebrow}
            lines={CONTACT.headline}
            compact
          />
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-mist sm:text-base">
              {CONTACT.paragraph}
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-12 border-l border-aqua/40 pl-6" data-testid="contact-address">
              <span className="label-mono flex items-center gap-2 text-aqua">
                <MapPin className="h-3.5 w-3.5" />
                Registered office
              </span>
              <p className="mt-4 text-sm font-medium uppercase tracking-[0.12em] text-offwhite">
                {CONTACT.company}
              </p>
              <address className="mt-3 space-y-1 text-sm not-italic leading-relaxed text-mist">
                {CONTACT.address.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </address>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="relative border border-border bg-[linear-gradient(160deg,hsl(var(--navy-800)/0.7),hsl(var(--navy-950)/0.9))] p-8 sm:p-12">
              {sent ? (
                <div
                  className="flex min-h-[340px] flex-col items-start justify-center"
                  data-testid="contact-success"
                >
                  <span className="flex h-12 w-12 items-center justify-center border border-aqua/60 bg-aqua/10">
                    <Check className="h-5 w-5 text-aqua" />
                  </span>
                  <h3 className="display mt-8 text-2xl text-offwhite sm:text-3xl">
                    Message received.
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-mist">
                    Thank you for contacting Montaire Group. Your enquiry has been
                    recorded and a member of our team will review it.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another"
                    className="label-mono mt-10 inline-flex items-center gap-2 text-offwhite"
                  >
                    <span className="aqua-underline pb-1">Send another message</span>
                    <ArrowRight className="h-3.5 w-3.5 text-aqua" />
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="contact-form">
                  <div className="grid gap-8 sm:grid-cols-2">
                    <div>
                      <label className="label-mono text-mist" htmlFor="contact-name">
                        Name
                      </label>
                      <input
                        id="contact-name"
                        data-testid="contact-input-name"
                        className={fieldClass}
                        value={values.name}
                        onChange={update("name")}
                        placeholder="Your full name"
                      />
                      {errors.name && (
                        <p className="mt-2 text-xs text-destructive" data-testid="contact-error-name">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="label-mono text-mist" htmlFor="contact-email">
                        Email
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        data-testid="contact-input-email"
                        className={fieldClass}
                        value={values.email}
                        onChange={update("email")}
                        placeholder="you@company.com"
                      />
                      {errors.email && (
                        <p className="mt-2 text-xs text-destructive" data-testid="contact-error-email">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-8">
                    <label className="label-mono text-mist" htmlFor="contact-company">
                      Company <span className="text-mist/50">(optional)</span>
                    </label>
                    <input
                      id="contact-company"
                      data-testid="contact-input-company"
                      className={fieldClass}
                      value={values.company}
                      onChange={update("company")}
                      placeholder="Company name"
                    />
                  </div>

                  <div className="mt-8">
                    <label className="label-mono text-mist" htmlFor="contact-message">
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      rows={4}
                      data-testid="contact-input-message"
                      className={`${fieldClass} resize-none`}
                      value={values.message}
                      onChange={update("message")}
                      placeholder="How can we help?"
                    />
                    {errors.message && (
                      <p className="mt-2 text-xs text-destructive" data-testid="contact-error-message">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="group mt-12 inline-flex items-center justify-center gap-3 border border-aqua/70 bg-aqua/10 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-offwhite transition-all duration-300 hover:border-aqua-bright hover:bg-aqua/20 hover:shadow-[0_0_28px_-10px_hsl(var(--aqua-bright))] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? "Sending" : "Send enquiry"}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
